// src/pages/maintenance/MaintenanceReportsPage.js
// Summary / cost / downtime / equipment-health reporting. See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import maintenanceService from '../../services/maintenanceService';
import MaintenanceNav from '../../components/MaintenanceNav';
import TrendBarChart from '../../components/charts/TrendBarChart';

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};
const HEADER_BG = '#1b2638';
const TYPE_COLORS = { reactive: '#dc3545', preventive: '#198754', inspection: '#0d6efd', calibration: '#6f42c1', safety: '#fd7e14' };

// Each tab's data is fetched lazily, only when first selected, and cached — firing all 4 report
// queries up front on mount tends to trip the per-company concurrent-request throttle
// (middleware/connectionThrottle.js) alongside the sidebar's own background polling, and 3 of
// the 4 tabs aren't even visible on first load anyway.
const TAB_LOADERS = {
  summary: () => maintenanceService.getSummaryReport(),
  cost: () => maintenanceService.getCostReport(),
  downtime: () => maintenanceService.getDowntimeReport(),
  overview: () => maintenanceService.getEquipmentOverview(),
};

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div className="col-md-3 col-sm-6">
      <div className="card h-100 border-0 shadow-sm">
        <div className="card-body py-3 d-flex align-items-center gap-3">
          <div style={{ fontSize: 26, lineHeight: 1 }}>{icon}</div>
          <div>
            <div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.3 }}>{label}</div>
            <div className="fw-bold" style={{ fontSize: 24, color }}>{value}</div>
            {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header fw-semibold" style={{ backgroundColor: HEADER_BG, color: '#fff' }}>{title}</div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function DateRangeFilter({ range, setRange, onApply, onClear }) {
  return (
    <div className="d-flex align-items-end gap-2 mb-3 flex-wrap">
      <div>
        <label className="form-label mb-1 text-muted" style={{ fontSize: 11 }}>From</label>
        <input type="date" className="form-control form-control-sm" value={range.start} onChange={e => setRange(r => ({ ...r, start: e.target.value }))} />
      </div>
      <div>
        <label className="form-label mb-1 text-muted" style={{ fontSize: 11 }}>To</label>
        <input type="date" className="form-control form-control-sm" value={range.end} onChange={e => setRange(r => ({ ...r, end: e.target.value }))} />
      </div>
      <button className="btn btn-sm btn-primary" onClick={onApply}>Apply</button>
      {(range.start || range.end) && <button className="btn btn-sm btn-outline-secondary" onClick={onClear}>Clear</button>}
    </div>
  );
}

// Small colored-square status badges — mirrors components/SuppliersHub.js's StatusBadge/
// ComplianceLegend pattern exactly (same sizing/radius), the established "at-a-glance compliance
// grid" visual language already used elsewhere in this app.
function SquareBadge({ cls, icon, title }) {
  return (
    <span
      className={`badge ${cls}`}
      title={title}
      style={{ width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, borderRadius: 6 }}
    >
      {icon}
    </span>
  );
}

const IN_USE_CFG = {
  operational: { cls: 'bg-success', icon: '✓', label: 'Operational' },
  under_maintenance: { cls: 'bg-warning text-dark', icon: '!', label: 'Under Maintenance' },
  out_of_service: { cls: 'bg-danger', icon: '✕', label: 'Out of Service' },
  decommissioned: { cls: 'bg-light text-secondary border', icon: '–', label: 'Decommissioned' },
};

const HEALTH_CFG = {
  unknown: { cls: 'bg-light text-secondary border', icon: '?', label: 'Not enough sensor history yet' },
  ok: { cls: 'bg-success', icon: '✓', label: 'No degrading trends detected' },
  flagged: { cls: 'bg-danger', icon: '⚠', label: 'Possible developing issue' },
};

const DUE_CFG = {
  none: { cls: 'bg-light text-secondary border', icon: '–', label: 'No PM scheduled' },
  ok: { cls: 'bg-success', icon: '✓', label: 'Not due soon' },
  due_soon: { cls: 'bg-warning text-dark', icon: '!', label: 'Due within a week' },
  overdue: { cls: 'bg-danger', icon: '✕', label: 'Overdue' },
};

const CRITICALITY_CLS = { critical: 'bg-danger', high: 'bg-warning text-dark', medium: 'bg-info-subtle text-info', low: 'bg-secondary-subtle text-secondary' };

function EquipmentOverviewTable({ rows, onGenerateWo, generatingId }) {
  if (!rows.length) return <p className="text-muted mb-0" style={{ fontSize: 13 }}>No active assets yet.</p>;
  return (
    <div>
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th style={{ minWidth: 200 }}>Equipment</th>
              <th className="text-center">In Use</th>
              <th className="text-center">Health</th>
              <th className="text-center">Maint. Due</th>
              <th className="text-center">Open WOs</th>
              <th>Criticality</th>
              <th>Last Serviced</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const inUse = IN_USE_CFG[r.status] || IN_USE_CFG.operational;
              const healthKey = r.healthFlagged === null ? 'unknown' : r.healthFlagged ? 'flagged' : 'ok';
              const health = HEALTH_CFG[healthKey];
              const due = DUE_CFG[r.maintenanceDueStatus] || DUE_CFG.none;
              return (
                <tr key={r.id}>
                  <td style={{ fontSize: 13, fontWeight: 600 }}><Link to={`/maintenance/assets/${r.id}`}>{r.name}</Link></td>
                  <td className="text-center"><SquareBadge cls={inUse.cls} icon={inUse.icon} title={inUse.label} /></td>
                  <td className="text-center">
                    <SquareBadge cls={health.cls} icon={health.icon} title={healthKey === 'flagged' ? (r.healthReasons || []).join('; ') : health.label} />
                  </td>
                  <td className="text-center"><SquareBadge cls={due.cls} icon={due.icon} title={due.label + (r.nextPmDueAt ? ` (${new Date(r.nextPmDueAt).toLocaleDateString()})` : '')} /></td>
                  <td className="text-center">
                    <span className={`badge ${r.openWorkOrderCount > 0 ? 'bg-primary' : 'bg-light text-secondary border'}`} style={{ fontSize: 11 }}>{r.openWorkOrderCount}</span>
                  </td>
                  <td><span className={`badge ${CRITICALITY_CLS[r.criticalityLevel] || 'bg-light text-secondary border'}`} style={{ fontSize: 11 }}>{labelize(r.criticalityLevel)}</span></td>
                  <td style={{ fontSize: 12 }} className="text-muted">{r.lastServicedAt ? new Date(r.lastServicedAt).toLocaleDateString() : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {healthKey === 'flagged' && (
                      r.healthWorkOrderId ? (
                        <Link to={`/maintenance/work-orders/${r.healthWorkOrderId}`} className="btn btn-sm btn-outline-danger">View WO</Link>
                      ) : (
                        <button className="btn btn-sm btn-danger" disabled={generatingId === r.id} onClick={() => onGenerateWo(r.id)}>
                          {generatingId === r.id ? '…' : '⚡ Generate WO'}
                        </button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex gap-3 flex-wrap px-1 pt-1">
        {[['In Use', IN_USE_CFG.operational], ['Under Maint.', IN_USE_CFG.under_maintenance], ['Out of Service', IN_USE_CFG.out_of_service]].map(([label, cfg]) => (
          <div key={label} className="d-flex align-items-center gap-1">
            <span className={`badge ${cfg.cls}`} style={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, borderRadius: 5 }}>{cfg.icon}</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{label}</span>
          </div>
        ))}
        {[['Healthy', HEALTH_CFG.ok], ['Flagged', HEALTH_CFG.flagged], ['No data yet', HEALTH_CFG.unknown]].map(([label, cfg]) => (
          <div key={label} className="d-flex align-items-center gap-1">
            <span className={`badge ${cfg.cls}`} style={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, borderRadius: 5 }}>{cfg.icon}</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{label}</span>
          </div>
        ))}
        {[['Due OK', DUE_CFG.ok], ['Due Soon', DUE_CFG.due_soon], ['Overdue', DUE_CFG.overdue]].map(([label, cfg]) => (
          <div key={label} className="d-flex align-items-center gap-1">
            <span className={`badge ${cfg.cls}`} style={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, borderRadius: 5 }}>{cfg.icon}</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MaintenanceReportsPage() {
  const [tab, setTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [cost, setCost] = useState(null);
  const [downtime, setDowntime] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);
  const [costRange, setCostRange] = useState({ start: '', end: '' });
  const [downtimeRange, setDowntimeRange] = useState({ start: '', end: '' });

  const setters = { summary: setSummary, cost: setCost, downtime: setDowntime, overview: setOverview };
  const data = { summary, cost, downtime, overview };

  useEffect(() => {
    if (data[tab] !== null) return;
    setLoading(true);
    TAB_LOADERS[tab]().then(setters[tab]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const reloadCost = (range) => {
    setLoading(true);
    maintenanceService.getCostReport(range).then(setCost).finally(() => setLoading(false));
  };
  const reloadDowntime = (range) => {
    setLoading(true);
    maintenanceService.getDowntimeReport(range).then(setDowntime).finally(() => setLoading(false));
  };

  const generateWo = async (assetId) => {
    setGeneratingId(assetId);
    try {
      await maintenanceService.generateHealthWorkOrder(assetId);
      setOverview(await maintenanceService.getEquipmentOverview()); // refetch so the row picks up the new healthWorkOrderId
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <>
      <MaintenanceNav />
      <div className="container-fluid py-3">
      <h4 className="mb-3">Maintenance Reports</h4>

      {/* Sticky below MaintenanceNav (top: var(--topbar-height)) — +37px ≈ MaintenanceNav's own
          rendered height, so both nav levels stay visible while scrolling a long table like
          Equipment Overview. */}
      <div style={{ position: 'sticky', top: 'calc(var(--topbar-height) + 37px)', zIndex: 998, background: '#fff' }}>
        <ul className="nav nav-tabs mb-3">
          {['summary', 'cost', 'downtime', 'overview'].map(k => (
            <li className="nav-item" key={k}>
              <button className={`nav-link ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{labelize(k)}</button>
            </li>
          ))}
        </ul>
      </div>

      {loading && data[tab] === null && <div className="text-center py-5 text-muted"><span className="spinner-border spinner-border-sm me-2" />Loading…</div>}

      {tab === 'summary' && summary && (
        <div className="d-flex flex-column gap-3">
          <div className="row g-3">
            <StatCard icon="🏭" label="Active Assets" value={summary.assetCount} />
            <StatCard icon="🛠️" label="Open Work Orders" value={summary.openWoCount} />
            <StatCard icon="⏰" label="Overdue" value={summary.overdueCount} color={summary.overdueCount > 0 ? '#dc3545' : undefined} />
            <StatCard icon="✅" label="Completed This Month" value={summary.completedThisMonth} color="#198754" />
            <StatCard icon="⏱️" label="Avg. Completion Time" value={fmtHours(summary.avgCompletionHours)} sub="create → complete" />
            {summary.manualCoverage && (
              <StatCard
                icon="📖" label="Manual Coverage"
                value={`${summary.manualCoverage.criticalAssetsWithManual} / ${summary.manualCoverage.criticalAssetCount}`}
                sub="food-safety-critical assets"
              />
            )}
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <ChartCard title="Work Orders by Status">
                <TrendBarChart
                  labels={summary.byStatus.map(s => labelize(s.status))}
                  values={summary.byStatus.map(s => parseInt(s.count, 10))}
                  yAxisTitle="Work Orders"
                  defaultColor={HEADER_BG}
                  height={240}
                />
              </ChartCard>
            </div>
            <div className="col-md-6">
              <ChartCard title="Work Orders by Type">
                <TrendBarChart
                  labels={summary.byType.map(t => labelize(t.type))}
                  values={summary.byType.map(t => parseInt(t.count, 10))}
                  colors={summary.byType.map(t => TYPE_COLORS[t.type] || HEADER_BG)}
                  yAxisTitle="Work Orders"
                  height={240}
                />
              </ChartCard>
            </div>
          </div>
        </div>
      )}

      {tab === 'cost' && cost && (
        <div className="d-flex flex-column gap-3">
          <DateRangeFilter range={costRange} setRange={setCostRange} onApply={() => reloadCost(costRange)} onClear={() => { setCostRange({ start: '', end: '' }); reloadCost({}); }} />

          <div className="row g-3">
            <StatCard icon="👷" label="Labor Cost" value={fmtMoney(cost.totalLaborCost)} />
            <StatCard icon="🔩" label="Parts Cost" value={fmtMoney(cost.totalPartsCost)} />
            <StatCard icon="💰" label="Total Cost" value={fmtMoney(cost.totalCost)} color="#198754" />
            <StatCard icon="📊" label="Avg. Cost / WO" value={fmtMoney(cost.avgCostPerWo)} sub={`${cost.workOrderCount || 0} completed WOs`} />
          </div>

          {cost.byType?.length > 0 && (
            <ChartCard title="Cost by Work Order Type">
              <TrendBarChart
                labels={cost.byType.map(t => labelize(t.type))}
                values={cost.byType.map(t => Math.round(t.totalCost))}
                colors={cost.byType.map(t => TYPE_COLORS[t.type] || HEADER_BG)}
                yAxisTitle="Cost ($)"
                height={240}
              />
            </ChartCard>
          )}

          <ChartCard title="Cost by Asset">
            {cost.byAsset.length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No completed work orders in this range.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light"><tr><th>Asset</th><th className="text-end">Work Orders</th><th className="text-end">Total Cost</th></tr></thead>
                  <tbody>
                    {cost.byAsset.map((a, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 13 }}>{a.assetName}</td>
                        <td className="text-end" style={{ fontSize: 13 }}>{a.count}</td>
                        <td className="text-end fw-semibold" style={{ fontSize: 13 }}>{fmtMoney(a.totalCost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </div>
      )}

      {tab === 'downtime' && downtime && (
        <div className="d-flex flex-column gap-3">
          <DateRangeFilter range={downtimeRange} setRange={setDowntimeRange} onApply={() => reloadDowntime(downtimeRange)} onClear={() => { setDowntimeRange({ start: '', end: '' }); reloadDowntime({}); }} />

          <div className="row g-3">
            <StatCard icon="⏳" label="Total Downtime" value={fmtHours(downtime.totalDowntimeHours)} />
            <StatCard icon="📉" label="Avg. Downtime / WO" value={fmtHours(downtime.avgDowntimeHours)} />
            <StatCard icon="🛠️" label="Work Orders" value={downtime.workOrderCount || 0} />
          </div>

          <ChartCard title="Downtime by Asset">
            {downtime.byAsset.length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No completed work orders with downtime in this range.</p>
            ) : (
              <>
                <TrendBarChart
                  labels={downtime.byAsset.slice(0, 10).map(a => a.assetName)}
                  values={downtime.byAsset.slice(0, 10).map(a => Math.round(a.totalDowntimeHours * 10) / 10)}
                  yAxisTitle="Downtime (hrs)"
                  defaultColor="#fd7e14"
                  height={240}
                />
                <div className="table-responsive mt-3">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light"><tr><th>Asset</th><th className="text-end">Work Orders</th><th className="text-end">Total Downtime</th></tr></thead>
                    <tbody>
                      {downtime.byAsset.map((a, i) => (
                        <tr key={i}>
                          <td style={{ fontSize: 13 }}>{a.assetName}</td>
                          <td className="text-end" style={{ fontSize: 13 }}>{a.workOrderCount}</td>
                          <td className="text-end fw-semibold" style={{ fontSize: 13 }}>{fmtHours(a.totalDowntimeHours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </ChartCard>
        </div>
      )}

      {tab === 'overview' && overview && (
        <ChartCard title="Equipment Overview">
          <EquipmentOverviewTable rows={overview} onGenerateWo={generateWo} generatingId={generatingId} />
        </ChartCard>
      )}
      </div>
    </>
  );
}
